/**
 * Who may open which board — the nav's half of the backend's two gates.
 *
 * Not every route in NOVA is open to every signed-in user. Two gates say so,
 * for different reasons and by different mechanisms:
 *
 * - **`adminOnly`** — the route sits behind `AdminRoute` in `App.tsx`
 *   (`/licences/accounts`). A non-admin is *bounced*, so the row is **hidden**:
 *   there is nothing behind the door for them, and showing it would be an
 *   invitation to a redirect.
 * - **`investigatorOnly`** — the *page* renders for anyone, but every payload
 *   on it answers **403** unless the session mail is on
 *   `NOVA_FO_INVESTIGATORS` (`/dynamics/investigate`, `/dynamics/reports`,
 *   `/dynamics/pinpoint`). The first two show one person at a time; Pinpoint
 *   names no people at all and is gated anyway, because ranking which company
 *   is worst is an operational judgement about named parts of the business.
 *   A non-investigator who opens it gets a fully-explained refusal, not a
 *   crash. So the row is **disabled with a sentence** rather than hidden: the
 *   gap is information ("the queue exists, you may not read it"), and hiding it
 *   would make the estate look like it has no report queue at all — the
 *   flattering-omission failure the design rules single out.
 *
 * **Three states, never two.** Investigator status is not a flag the frontend
 * holds; it is derived from what the API answers (see `useFoInvestigatorAccess`
 * in `lib/api/hooks/fo-console.ts`, which reads the reports list and reports
 * `denied` on its 403). Until the first answer lands it is `unknown`, and an
 * unknown gate must not lock the door: a reader who *is* an investigator would
 * otherwise watch their own screens grey out for the first fraction of a
 * second of every session, while the cost of guessing the other way is a page
 * that says, in plain words, that this sign-in is not on the list. So `unknown`
 * renders the link and the destination page carries the truth.
 *
 * This module holds only the decision, no icons and no JSX, so `node --test`
 * can exercise it directly (`tests/nav-access.test.mjs`).
 */

/** Whether the reader is on the F&O investigator list, as far as the API has said. */
export type NavAccess = "granted" | "denied" | "unknown";

/** What the nav knows about the current reader. */
export interface NavViewer {
  /** `me.role === "admin"` — the `AdminRoute` guard's own test. */
  isAdmin: boolean;
  /** Derived from the API, never from an address list in the frontend. */
  investigator: NavAccess;
}

/** How a nav row renders for this reader. */
export type BoardVisibility =
  /** A real link. */
  | "link"
  /** Rendered, not navigable, with {@link INVESTIGATOR_ONLY_LINE} beside it. */
  | "disabled"
  /** Not rendered at all — the guard would bounce them off it. */
  | "hidden";

/** The gates a registry entry can declare. Structural, so `NavSection` satisfies it. */
export interface GatedBoard {
  adminOnly?: boolean;
  investigatorOnly?: boolean;
}

/**
 * The one plain sentence a disabled investigator-gated row carries.
 *
 * Names the setting rather than the people in it: the list is configuration an
 * IT manager changes, and printing colleagues' addresses into every sidebar
 * would be a small directory leak for no gain.
 *
 * It also has to fit all three gated rows, because it is rendered once under
 * the group and again as each disabled row's `title`. Saying "one person at a
 * time" alone would put that claim on Pinpoint, which carries no identities —
 * so the sentence gives the two per-person screens and Pinpoint their own
 * reason, in the words their own pages use.
 */
export const INVESTIGATOR_ONLY_LINE =
  "Investigate and Reports show one person at a time, and Pinpoint ranks named parts of the business, so all three open only for the investigators named in NOVA's configuration.";

/** How one board renders for one reader. See the module comment for the three rules. */
export function boardVisibility(board: GatedBoard, viewer: NavViewer): BoardVisibility {
  if (board.adminOnly === true && !viewer.isAdmin) {
    return "hidden";
  }
  if (board.investigatorOnly === true && viewer.investigator === "denied") {
    return "disabled";
  }
  return "link";
}

/** Whether a reader may follow a board's link — the test a caller with no row makes. */
export function canOpenBoard(board: GatedBoard, viewer: NavViewer): boolean {
  return boardVisibility(board, viewer) === "link";
}
